import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES } from '../errors/errorMessages';
import type { ServiceRequestStatus } from '../types/serviceRequestListType';
import type { AdminNote } from '../types/viewServiceRequestType';
import type { ServiceRequestAttachmentMedia } from './serviceRequestPresignedUrlsApiService';
import serviceRequestEndpoints from './serviceRequestEndpoints';

export interface UpdateServiceRequestStatusParams {
  serviceRequestId: string;
  newStatus: ServiceRequestStatus;
  adminNotes?: string;
  notify?: boolean;
  /**
   * Missed-visit reason code (RR-622) — only valid when `newStatus` is
   * `unfulfilled`. Sent as `missed_visit_reason`; the BE rejects it for any
   * other target. When the reason is `other` the BE additionally requires
   * `admin_notes` to carry the free-text comment.
   */
  missedVisitReason?: string;
  /**
   * Completion attachments — only sent when `newStatus` is `completed`
   * (admin-assignee flow); ignored for any other target. Each item is a
   * fully-uploaded media resource (S3 key in `path`). On completion these and
   * `adminNotes` are sent together under a `response: { attachments, notes }`
   * object. Completing requires `adminNotes` OR at least one attachment.
   */
  attachments?: ServiceRequestAttachmentMedia[];
}

export interface UpdateServiceRequestStatusResponseData {
  id: string;
  service_request_id: string;
  previous_status: ServiceRequestStatus;
  new_status: ServiceRequestStatus;
  admin_notes: AdminNote[] | null;
  updated_at: string;
  notification_sent: boolean;
}

export interface UpdateServiceRequestStatusResult {
  success: boolean;
  data?: UpdateServiceRequestStatusResponseData;
  error?: string;
  /** True only on HTTP 403 — caller lacks the AVP privilege or doesn't own the task. */
  permissionDenied?: boolean;
}

const STATUS_LABEL_MAP: Record<string, string> = {
  pending: 'Oczekuje',
  in_review: 'W trakcie weryfikacji',
  assigned: 'Przydzielone',
  accepted: 'Zaakceptowane',
  rejected: 'Odrzucone',
  in_progress: 'W realizacji',
  delayed: 'Opóźnione',
  completed: 'Zakończone',
  closed: 'Zamknięte',
  resolved: 'Rozwiązane',
  cancelled: 'Anulowane',
  unfulfilled: 'Niezrealizowane',
};

const STATUS_KEY_RE =
  /pending|in_review|assigned|accepted|rejected|in_progress|delayed|completed|closed|resolved|cancelled|unfulfilled/;

const TRANSITION_ERROR_RE = new RegExp(
  `Cannot change service request status from (${STATUS_KEY_RE.source}) to (${STATUS_KEY_RE.source})`,
  'i'
);

const ALREADY_STATUS_RE = new RegExp(
  `Service request is already (${STATUS_KEY_RE.source})`,
  'i'
);

const mapStatusNames = (msg: string): string => {
  const alreadyMatch = ALREADY_STATUS_RE.exec(msg);
  if (alreadyMatch && alreadyMatch[1]) {
    const label = STATUS_LABEL_MAP[alreadyMatch[1]] ?? alreadyMatch[1];
    return `Zgłoszenie jest już w statusie „${label}".`;
  }
  const transitionMatch = TRANSITION_ERROR_RE.exec(msg);
  if (transitionMatch && transitionMatch[1] && transitionMatch[2]) {
    const from = STATUS_LABEL_MAP[transitionMatch[1]] ?? transitionMatch[1];
    const to = STATUS_LABEL_MAP[transitionMatch[2]] ?? transitionMatch[2];
    return `Nie można zmienić statusu zgłoszenia z „${from}" na „${to}".`;
  }
  return msg.replace(
    new RegExp(`\\b(${STATUS_KEY_RE.source})\\b`, 'g'),
    (key) => STATUS_LABEL_MAP[key] ?? key
  );
};

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401 || status === 403) {
    return UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (errorData && typeof errorData === 'object') {
    const obj = errorData as { message?: string; error?: string };
    if (typeof obj.error === 'string' && obj.error.trim().length > 0) {
      return mapStatusNames(obj.error);
    }
    if (typeof obj.message === 'string' && obj.message.trim().length > 0) {
      return mapStatusNames(obj.message);
    }
  }
  if (typeof errorData === 'string' && errorData.trim().length > 0) {
    return mapStatusNames(errorData);
  }
  return UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.DEFAULT_ERROR;
};

export const updateServiceRequestStatus = async ({
  serviceRequestId,
  newStatus,
  adminNotes,
  notify,
  missedVisitReason,
  attachments,
}: UpdateServiceRequestStatusParams): Promise<UpdateServiceRequestStatusResult> => {
  const trimmedId = serviceRequestId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  const trimmedNotes = typeof adminNotes === 'string' ? adminNotes.trim() : '';

  const body: Record<string, unknown> = { new_status: newStatus };
  if (missedVisitReason) {
    body['missed_visit_reason'] = missedVisitReason;
  }
  if (notify) {
    body['notify'] = true;
  }
  if (newStatus === 'completed') {
    // Completion sends its documents and note under a single `response`
    // object. The BE now rejects a top-level `attachments` array and ignores
    // `admin_notes` on this transition, so route both through `response`
    // (empty note -> null per the contract).
    body['response'] = {
      attachments: attachments ?? [],
      notes: trimmedNotes.length > 0 ? trimmedNotes : null,
    };
  } else if (trimmedNotes.length > 0) {
    body['admin_notes'] = trimmedNotes;
  }

  try {
    const result = await apiService.patchAPI(
      serviceRequestEndpoints.UPDATE_SERVICE_REQUEST_STATUS(trimmedId),
      body,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        data?: UpdateServiceRequestStatusResponseData;
      };
      return {
        success: true,
        ...(payload.data ? { data: payload.data } : {}),
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
      ...(result.status === 403 ? { permissionDenied: true } : {}),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(
          error.response?.data,
          error.response?.status
        ),
        ...(error.response?.status === 403 ? { permissionDenied: true } : {}),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
